import {
  Alert,
  AlertDescription,
  Button,
  Icon,
  Stack,
  Text,
} from '@eluve/components';

import { PdfPreview } from './PdfPreview';

const MAX_VISIBLE_FIELDS = 5;

const truncatedFieldList = (fields: string[]): string =>
  fields.slice(0, MAX_VISIBLE_FIELDS).join(', ') +
  (fields.length > MAX_VISIBLE_FIELDS
    ? ` +${fields.length - MAX_VISIBLE_FIELDS} more`
    : '');

const FillNotice: React.FC<{
  warning?: boolean;
  children: React.ReactNode;
}> = ({ warning, children }) => (
  <Alert variant={warning ? 'warning' : undefined}>
    <Icon name={warning ? 'AlertTriangle' : 'Info'} />
    <AlertDescription>{children}</AlertDescription>
  </Alert>
);

export type PdfViewerContentProps = {
  /** A URL the browser can render in <object> (signed GCS URL or blob URL). */
  fileUrl: string;
  fileName: string;
  onDownload: () => void;
  skippedFields?: string[];
  alteredFields?: string[];
  usedAppearanceFallback?: boolean;
  excludedHumanOnlyFields?: string[];
};

/**
 * Read-only filled-PDF view: filename + download, fill warning callouts, and an
 * inline preview. Shared by the in-flight fill result (PdfFillTabContent) and
 * the persisted document tab (PdfDocumentTabContent).
 */
export const PdfViewerContent: React.FC<PdfViewerContentProps> = ({
  fileUrl,
  fileName,
  onDownload,
  skippedFields = [],
  alteredFields = [],
  usedAppearanceFallback = false,
  excludedHumanOnlyFields = [],
}) => (
  <Stack className="gap-[26px]" fullHeight align="stretch">
    <Stack justify="between" gap={2} direction="horizontal">
      <Text weight="semibold" className="truncate">
        {fileName}
      </Text>
      <Button size="sm" onClick={onDownload}>
        <Icon name="Download" size="auto" />
        Download
      </Button>
    </Stack>
    {skippedFields.length > 0 && (
      <FillNotice warning>
        {skippedFields.length} field
        {skippedFields.length === 1 ? '' : 's'} couldn&apos;t be filled
        automatically — review before using: {truncatedFieldList(skippedFields)}
      </FillNotice>
    )}
    {alteredFields.length > 0 && (
      <FillNotice warning>
        {alteredFields.length} field
        {alteredFields.length === 1 ? ' was' : 's were'} adjusted to fit the
        form (unsupported characters or length) — review:{' '}
        {truncatedFieldList(alteredFields)}
      </FillNotice>
    )}
    {usedAppearanceFallback && (
      <FillNotice warning>
        This PDF saved with compatibility fallback — some viewers may render
        fields differently. Review before sending.
      </FillNotice>
    )}
    {excludedHumanOnlyFields.length > 0 && (
      <FillNotice>
        {excludedHumanOnlyFields.length} field
        {excludedHumanOnlyFields.length === 1 ? '' : 's'} intentionally left for
        manual completion (signatures, consent, etc.)
      </FillNotice>
    )}
    {/* react-pdf (canvas) renders inline even for cross-origin signed URLs; a
        native <object> renders blank / force-downloads them in Chrome. */}
    <PdfPreview file={fileUrl} className="max-h-[70vh]" />
  </Stack>
);
